import { Pressable, SxProp, View } from 'dripsy';
import React, { useState } from 'react';

import { Icon } from '@/design-system/components/icons/Icon';
import { UniButton } from '@/design-system/components/buttons/UniButton';
import { UniAssetCard } from '@/design-system/components/cards/UniAssetCard';
import { UniSkeleton } from '@/design-system/components/cards';
import { TextBody, TextCaption, TextTitle } from '@/design-system/components/typography/UniTypography';

export interface PlatformAssets {
  id: string;
  platformName: string;
  product: 'Staking' | 'Fixed Income' | 'DEFI' | 'Testing';
  providers: Array<{
    id: string;
    estApy: string;
    tenor: string;
  }>;
  isLast?: boolean;
}

export interface ExpandableAsset {
  asset: string;
  assetName: string;
  estApy: string;
  isOwnedAsset: boolean;
  platforms: Array<PlatformAssets>;
}

export interface UniExpandableAssetCardProps {
  details?: ExpandableAsset;
  loading?: boolean;
}

export const UniExpandableAssetCard: React.FC<UniExpandableAssetCardProps> = (props) => {
  const { loading, details } = props;

  if (loading) return <UniExpandableAssetCardOnLoad />;
  if (!details) return null;

  const { asset, assetName, estApy, platforms } = details;

  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  return (
    <View sx={sxStyles.container}>
      {/* Top */}
      <Pressable onPress={() => setIsExpanded(!isExpanded)}>
        <View sx={sxStyles.containerTop}>
          {/* ICON */}
          <UniAssetCard asset={asset} subText={assetName} />
          {/* Est APY */}
          <View sx={sxStyles.estApyWrapper}>
            <TextCaption secondary sx={{ textAlign: 'right' }}>
              Est. APY
            </TextCaption>
            <View sx={sxStyles.estApyDetails}>
              <TextCaption secondary sx={{ mr: 3 }}>
                up to
              </TextCaption>
              <TextCaption bold success>
                {estApy}
              </TextCaption>
            </View>
          </View>
          {/* Toggle */}
          <View sx={sxStyles.toggleWrapper}>
            <Icon name={isExpanded ? 'arrowUp' : 'arrowDown'} />
          </View>
        </View>
      </Pressable>
      {/* Bottom */}
      {isExpanded && (
        <View sx={sxStyles.containerBottom}>
          {/* List of Platform Items */}
          {platforms.map((platform, i) => (
            <UniPlatformAssetCard {...platform} key={`platform-${i}`} isLast={i === platforms.length - 1} />
          ))}
        </View>
      )}
    </View>
  );
};

const UniPlatformAssetCard: React.FC<PlatformAssets> = (props) => {
  const { platformName, product, providers, isLast } = props;

  return (
    <View sx={isLast ? platformAssetCardSxStyles.container : platformAssetCardSxStyles.containerNoBorder}>
      {/* Top */}
      <View sx={platformAssetCardSxStyles.containerTop}>
        <TextBody bold>{platformName}</TextBody>
        <TextCaption>{product}</TextCaption>
      </View>
      <View sx={platformAssetCardSxStyles.title}>
        <View sx={platformAssetCardSxStyles.col1}>
          <TextCaption secondary>Est. APY</TextCaption>
        </View>
        <View sx={platformAssetCardSxStyles.col2}>
          <TextCaption secondary>Tenor</TextCaption>
        </View>
        <View sx={platformAssetCardSxStyles.col3}></View>
      </View>
      {/* Platform Items */}
      <View sx={platformAssetCardSxStyles.itemsWrapper}>
        {providers.map((provider, i) => {
          const isLast = i === providers.length - 1;
          return (
            <View sx={isLast ? platformAssetCardSxStyles.itemLast : platformAssetCardSxStyles.item} key={provider.id}>
              <View sx={platformAssetCardSxStyles.col1}>
                <TextTitle bold success>
                  {provider.estApy}
                </TextTitle>
              </View>
              <View sx={platformAssetCardSxStyles.col2}>
                <TextTitle>{provider.tenor}</TextTitle>
              </View>
              <View sx={{ ...platformAssetCardSxStyles.col3 }}>
                <UniButton label="Subscribe" size="small" variant="secondary1" />
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );
};

const UniExpandableAssetCardOnLoad = () => (
  <View sx={sxStyles.container}>
    <View sx={sxStyles.containerTop}>
      {/* ICON */}
      <View sx={sxStyles.iconWrapper}>
        <UniSkeleton style={{ height: 30, width: 30 }} shape="round" />
      </View>
      {/* Details */}
      <View sx={sxStyles.detailsWrapper}>
        <UniSkeleton style={{ height: 19, width: 55, marginBottom: 4 }} />
        <UniSkeleton style={{ height: 16, width: 155 }} />
      </View>
      {/* Est APY */}
      <View sx={sxStyles.estApyWrapper}>
        <UniSkeleton
          style={{
            height: 16,
            width: 55,
            marginBottom: 1,
            alignSelf: 'flex-end'
          }}
        />
        <UniSkeleton style={{ height: 19, width: 85 }} />
      </View>
      <View sx={sxStyles.toggleWrapper}>
        <UniSkeleton style={{ height: 20, width: 20 }} shape="round" />
      </View>
    </View>
  </View>
);

const sxStyles: Record<string, SxProp> = {
  container: {
    mb: 12,
    padding: 12,
    width: '100%',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '$borderPrimary',
    borderRadius: 5
  },
  containerTop: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  iconWrapper: {
    mr: 8,
    height: 30,
    width: 30
  },
  detailsWrapper: {
    flex: 1
  },
  estApyWrapper: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'flex-end'
  },
  estApyDetails: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'baseline'
  },
  toggleWrapper: {
    ml: 12
  },
  containerBottom: {
    mt: 12,
    borderTopWidth: 1,
    borderTopStyle: 'solid',
    borderTopColor: '$borderPrimary'
  }
};

const platformAssetCardSxStyles: { [key: string]: SxProp } = {
  container: {
    mt: 12
  },
  containerNoBorder: {
    mt: 12,
    pb: 12,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: '$borderPrimary'
  },
  containerTop: {
    mb: 12,
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  title: {
    mb: 8,
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  col1: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center'
  },
  col2: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center'
  },
  col3: {
    flex: 0.6
  },
  itemsWrapper: {},
  item: {
    mb: 11.5,
    pb: 12.5,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: '$borderPrimary',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  itemLast: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
};
